import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, ClipboardCopy, Pencil, Plus, Search, ShoppingBag, ShoppingCart, Tags, X } from 'lucide-react';
import { getProducts, renameCategory, nivelStock, porComprar, NIVEL, Product } from '../../../services/productsApi';
import { api } from '../../../services/apiClient';
import EmptyState from '../../../components/EmptyState';
import { Skeleton } from '../../../components/Skeleton';
import { useToast } from '../../../context/ToastContext';
import ProductoFicha from '../components/ProductoFicha';
import AvisoConteoVitrina from '../components/ConteoVitrina';
import { getVitrinaStatus, EVENTO_VITRINA, permisosVitrina, RespuestaPermisos } from '../vitrinaApi';

const money = (n: number) => `$${n.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
const SIN_CAT = '__sin__';
const POR_SURTIR = '__surtir__';
const etiqueta = (p: Product) => (p.brand ? `${p.brand} — ${p.name}` : p.name);
const chipCls = (on: boolean) =>
  `rounded-full px-3 py-1 text-xs font-medium transition ${on ? 'bg-accent text-on-accent' : 'border border-line text-content-2 hover:bg-surface-2'}`;

/** Categorías: renombrar (la categoría es texto del producto, así que renombrar las cambia todas). */
function CategoriasModal({ rows, onClose, onChanged }: { rows: Product[]; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const [editando, setEditando] = useState<string | null>(null);
  const [nombre, setNombre] = useState('');
  const [saving, setSaving] = useState(false);
  const cats = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((p) => { if (p.category) m.set(p.category, (m.get(p.category) || 0) + 1); });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es'));
  }, [rows]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function guardar(from: string) {
    const to = nombre.trim();
    if (!to) { toast.error('Escribe el nombre de la categoría'); return; }
    if (to === from) { setEditando(null); return; }
    setSaving(true);
    try {
      const r = await renameCategory(from, to);
      toast.success(`«${from}» ahora es «${to}» (${r.changed} producto${r.changed === 1 ? '' : 's'})`);
      setEditando(null);
      onChanged();
    } catch (e: any) { toast.error(e?.message || 'No se pudo renombrar'); }
    finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="Categorías">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content"><Tags size={18} className="text-brand-blue" /> Categorías</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <ul className="divide-y divide-line rounded-xl border border-line">
          {cats.map(([c, n]) => (
            <li key={c} className="flex items-center gap-2 px-3 py-2 text-sm">
              {editando === c ? (
                <>
                  <input autoFocus value={nombre} maxLength={40} onChange={(e) => setNombre(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') guardar(c); }}
                    className="min-w-0 flex-1 rounded-lg border border-line bg-surface px-2 py-1 text-sm text-content outline-none focus:border-brand-blue" />
                  <button disabled={saving} onClick={() => guardar(c)} className="rounded-lg bg-accent-solid px-2.5 py-1 text-xs font-semibold text-on-accent disabled:bg-line">Guardar</button>
                  <button onClick={() => setEditando(null)} className="text-xs text-content-3 hover:text-content">Cancelar</button>
                </>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate text-content">{c}</span>
                  <span className="text-xs tabular-nums text-content-3">{n} producto{n === 1 ? '' : 's'}</span>
                  <button onClick={() => { setEditando(c); setNombre(c); }} title="Renombrar"
                    className="rounded p-1 text-content-3 transition hover:text-content"><Pencil size={14} /></button>
                </>
              )}
            </li>
          ))}
          {cats.length === 0 && <li className="px-3 py-3 text-sm text-content-3">Todavía no hay categorías.</li>}
        </ul>
        <p className="mt-3 text-xs text-content-3">
          Para crear una categoría, abre un producto y elige <b className="text-content-2">＋ Nueva categoría…</b> en su ficha.
          Una categoría desaparece sola cuando ya no tiene productos.
        </p>
      </div>
    </div>, document.body);
}

/** QUÉ COMPRAR: lo que está en rojo o ámbar, rojos primero, con cuánto falta para el tope.
 *  «Copiar lista» deja el texto listo para pegarlo en el WhatsApp del proveedor o de quien surte. */
function QueComprarModal({ rows, onClose, onAbrir }: { rows: Product[]; onClose: () => void; onAbrir: (p: Product) => void }) {
  const toast = useToast();
  const low = useMemo(() => rows
    .filter((p) => p.isActive)
    .map((p) => ({ p, nivel: nivelStock(p), buy: porComprar(p) }))
    .filter((x): x is { p: Product; nivel: 'rojo' | 'ambar'; buy: number | null } => x.nivel === 'rojo' || x.nivel === 'ambar')
    .sort((a, b) => (a.nivel === b.nivel ? (a.p.stock ?? 0) - (b.p.stock ?? 0) : a.nivel === 'rojo' ? -1 : 1)), [rows]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  async function copiar() {
    const hoy = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long' });
    const texto = [`Vitrina · por surtir (${hoy})`, ...low.map((x) => `${x.nivel === 'rojo' ? '🔴' : '🟡'} ${etiqueta(x.p)}: quedan ${x.p.stock}${x.buy ? ` → comprar ${x.buy}` : ''}`)].join('\n');
    try { await navigator.clipboard.writeText(texto); toast.success('Lista copiada'); }
    catch { toast.error('No se pudo copiar; selecciona el texto a mano'); }
  }
  return createPortal(
    <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="Qué comprar">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="que-comprar" className="modal-panel relative flex max-h-[88vh] w-full max-w-md flex-col rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content"><ShoppingCart size={18} className="text-brand-blue" /> Qué comprar</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          {low.length === 0 ? (
            <p className="py-4 text-sm text-content-3">Nada en rojo ni en ámbar. Todo lo que lleva semáforo está surtido.</p>
          ) : (
            <ul className="divide-y divide-line">
              {low.map(({ p, nivel, buy }) => (
                <li key={p.id}>
                  <button onClick={() => onAbrir(p)} className="flex w-full items-center gap-3 py-2 text-left transition hover:bg-surface-2">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${NIVEL[nivel].punto}`} />
                    <span className="min-w-0 flex-1 truncate text-sm text-content">{etiqueta(p)}</span>
                    <span className="shrink-0 text-xs text-content-3">quedan <b className={`text-sm tabular-nums ${NIVEL[nivel].texto}`}>{p.stock}</b></span>
                    <span className="w-24 shrink-0 text-right text-xs text-content-2">{buy ? <>comprar <b className="text-sm text-content">{buy}</b></> : <span className="text-content-3">sin tope</span>}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-line px-5 py-3">
          <p className="text-xs text-content-3">Toca un producto para ajustar sus umbrales.</p>
          {low.length > 0 && (
            <button onClick={copiar} className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
              <ClipboardCopy size={15} /> Copiar lista
            </button>
          )}
        </div>
      </div>
    </div>, document.body);
}

/** Vitrina — catálogo de productos (AyB, merch, vestuario…).
 *
 *  La lista es de SOLO LECTURA: un clic en el renglón abre la ficha, y ahí se cambia todo
 *  (foto, descripción, categoría, precio, existencias) con un botón Guardar. Antes se escribía
 *  encima de la tabla, nadie lo descubría y un precio así se mueve sin querer (29-sep-2026).
 *  La VENTA se hace desde la Caja. */
export default function VitrinaPage() {
  const toast = useToast();
  const [rows, setRows] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<string | null>(null);
  // Ficha abierta: un producto, 'nuevo' o nada.
  const [ficha, setFicha] = useState<Product | 'nuevo' | null>(null);
  const [verCats, setVerCats] = useState(false);
  const [verCompra, setVerCompra] = useState(false);
  // Cambiar el catálogo pide nivel `manage` en el servidor; quien no lo tiene ve la ficha sin Guardar
  // en vez de toparse con un 403 al final. Mientras no se sabe, se asume que sí (el servidor manda).
  const [puedeEditar, setPuedeEditar] = useState(true);
  // Contar y anotar entradas pide `operate` (recepción); solo mirar, `view`.
  const [puedeContar, setPuedeContar] = useState(true);
  // «Llegó mercancía» (entrada, vitrina ≥ operate) va APARTE de contar desde el 1-oct-2026.
  const [puedeEntrada, setPuedeEntrada] = useState(true);
  // Inventario encendido (ajuste del estudio): la ficha enseña entradas y movimientos.
  const [inventario, setInventario] = useState(false);

  const reload = () => getProducts(true).then((r) => setRows(r.products)).finally(() => setLoading(false));
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    const leer = () => getVitrinaStatus().then((r) => setInventario(r.enabled)).catch(() => setInventario(false));
    leer();
    // Una venta, un fiado o un conteo en otra pantalla cambian las existencias que se ven aquí.
    const h = () => { leer(); reload(); };
    window.addEventListener(EVENTO_VITRINA, h);
    return () => window.removeEventListener(EVENTO_VITRINA, h);
  }, []);
  useEffect(() => {
    // Misma regla que el servidor (vitrinaApi.permisosVitrina): la cajera CUENTA pero no anota entradas.
    api.get<RespuestaPermisos>('/me/permissions')
      .then((r) => {
        const pv = permisosVitrina(r);
        setPuedeEditar(pv.editar); setPuedeContar(pv.contar); setPuedeEntrada(pv.entrada);
      }).catch(() => {});
  }, []);

  const categorias = useMemo(
    () => ([...new Set(rows.map((p) => p.category).filter(Boolean))] as string[]).sort((a, b) => a.localeCompare(b, 'es')),
    [rows]);
  const haySinCat = rows.some((p) => !p.category);
  // Semáforo (solo con el inventario encendido: apagado, las existencias no son de fiar).
  const nivelDe = (p: Product) => (inventario ? nivelStock(p) : null);
  const porSurtir = rows.filter((p) => p.isActive && ['rojo', 'ambar'].includes(nivelDe(p) || '')).length;
  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((p) =>
      (cat == null || (cat === SIN_CAT ? !p.category : cat === POR_SURTIR ? ['rojo', 'ambar'].includes(nivelDe(p) || '') : p.category === cat))
      && (!t || `${p.name} ${p.brand || ''} ${p.description || ''} ${p.sku || ''}`.toLowerCase().includes(t)));
  }, [rows, q, cat, inventario]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Si la categoría filtrada se quedó sin productos (renombrada o vaciada), se suelta el filtro.
  useEffect(() => {
    if (cat && cat !== SIN_CAT && cat !== POR_SURTIR && !categorias.includes(cat)) setCat(null);
    if (cat === SIN_CAT && !haySinCat) setCat(null);
    if (cat === POR_SURTIR && porSurtir === 0) setCat(null);
  }, [categorias, haySinCat, cat, porSurtir]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="mb-1 flex items-center gap-2 font-display text-2xl font-bold text-content"><ShoppingBag className="text-brand-blue" size={24} /> Vitrina</h2>
          <p className="text-sm text-content-3">
            Catálogo de productos. {puedeEditar ? 'Toca un producto para editarlo.' : 'Toca un producto para ver su ficha.'} La venta se hace desde la <b className="text-content-2">Caja</b>.
          </p>
        </div>
        {(puedeEditar || inventario) && (
          <div className="flex gap-2">
            {inventario && rows.length > 0 && (
              <button onClick={() => setVerCompra(true)} data-testid="btn-que-comprar"
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition ${porSurtir ? 'border-amber-500/50 bg-amber-500/10 text-content hover:bg-amber-500/20' : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                <ShoppingCart size={15} /> Qué comprar{porSurtir ? ` · ${porSurtir}` : ''}
              </button>
            )}
            {puedeEditar && categorias.length > 0 && (
              <button onClick={() => setVerCats(true)}
                className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">
                <Tags size={15} /> Categorías
              </button>
            )}
            {puedeEditar && (
              <button onClick={() => setFicha('nuevo')}
                className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
                <Plus size={16} /> Nuevo producto
              </button>
            )}
          </div>
        )}
      </div>

      <AvisoConteoVitrina detalle puedeContar={puedeContar} onCambio={reload} />

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState icon={ShoppingBag} title="Vitrina vacía" subtitle="Agrega tus productos (agua, snacks, merch…) y véndelos desde la Caja." />
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto…"
                className="w-full rounded-lg border border-line bg-surface py-2 pl-8 pr-3 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setCat(null)} className={chipCls(cat == null)}>Todo · {rows.length}</button>
              {categorias.map((c) => (
                <button key={c} onClick={() => setCat(cat === c ? null : c)} className={chipCls(cat === c)}>
                  {c} · {rows.filter((p) => p.category === c).length}
                </button>
              ))}
              {haySinCat && (
                <button onClick={() => setCat(cat === SIN_CAT ? null : SIN_CAT)} className={chipCls(cat === SIN_CAT)}>
                  Sin categoría · {rows.filter((p) => !p.category).length}
                </button>
              )}
              {inventario && porSurtir > 0 && (
                <button onClick={() => setCat(cat === POR_SURTIR ? null : POR_SURTIR)} data-testid="chip-por-surtir"
                  className={`${chipCls(cat === POR_SURTIR)} ${cat === POR_SURTIR ? '' : '!border-amber-500/50'}`}>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500 align-middle" />Por surtir · {porSurtir}
                </button>
              )}
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
            <div className="hidden grid-cols-[3rem_minmax(0,1fr)_8rem_6rem_6rem_1.25rem] items-center gap-3 border-b border-line bg-surface-2 px-3 py-2 text-xs font-medium uppercase tracking-wide text-content-3 md:grid">
              <span />
              <span>Producto</span>
              <span>Categoría</span>
              <span className="text-right">Precio</span>
              <span className="text-right">Existencias</span>
              <span />
            </div>
            <ul className="divide-y divide-line">
              {visibles.map((p) => (
                <li key={p.id}>
                  <button onClick={() => setFicha(p)} data-testid="producto-renglon"
                    className={`grid w-full grid-cols-[3rem_minmax(0,1fr)_auto_1.25rem] items-center gap-3 px-3 py-2 text-left transition hover:bg-surface-2 md:grid-cols-[3rem_minmax(0,1fr)_8rem_6rem_6rem_1.25rem] ${p.isActive ? '' : 'opacity-55'}`}>
                    {p.photoUrl ? (
                      <img src={p.photoUrl} alt="" loading="lazy" className="h-12 w-12 rounded-lg border border-line object-cover" />
                    ) : (
                      <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-line bg-surface-2 text-content-3"><ShoppingBag size={18} /></span>
                    )}
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-content">{p.name}</span>
                        {!p.isActive && <span className="shrink-0 rounded-full border border-line px-1.5 text-[11px] text-content-3">Apagado</span>}
                      </span>
                      <span className="block truncate text-xs text-content-3">
                        {[p.brand, p.description].filter(Boolean).join(' · ') || <span className="md:hidden">{p.category || 'Sin categoría'}</span>}
                      </span>
                    </span>
                    <span className="hidden truncate text-sm text-content-2 md:block">{p.category || <span className="text-content-3">—</span>}</span>
                    {/* Móvil: precio y existencias apilados en una sola columna. */}
                    <span className="text-right md:hidden">
                      <span className="block text-sm font-semibold tabular-nums text-content">{money(p.price)}</span>
                      <span className={`block text-xs tabular-nums ${nivelDe(p) ? NIVEL[nivelDe(p)!].texto : 'text-content-3'}`}>
                        {nivelDe(p) && <span className={`mr-1 inline-block h-2 w-2 rounded-full align-middle ${NIVEL[nivelDe(p)!].punto}`} />}
                        {p.stock != null ? `${p.stock} pza${p.stock === 1 ? '' : 's'}` : 'sin control'}
                      </span>
                    </span>
                    <span className="hidden text-right text-sm font-semibold tabular-nums text-content md:block">{money(p.price)}</span>
                    <span className={`hidden items-center justify-end gap-1.5 text-right text-sm tabular-nums md:flex ${nivelDe(p) ? `font-semibold ${NIVEL[nivelDe(p)!].texto}` : 'text-content-2'}`} data-nivel={nivelDe(p) || ''}>
                      {nivelDe(p) && <span className={`inline-block h-2.5 w-2.5 rounded-full ${NIVEL[nivelDe(p)!].punto}`} title={NIVEL[nivelDe(p)!].txt} />}
                      {p.stock != null ? p.stock : <span className="text-xs font-normal text-content-3">sin control</span>}
                    </span>
                    <ChevronRight size={16} className="text-content-3" />
                  </button>
                </li>
              ))}
              {visibles.length === 0 && <li className="px-4 py-6 text-center text-sm text-content-3">Ningún producto coincide.</li>}
            </ul>
          </div>
          <p className="mt-2 text-xs text-content-3">
            {rows.filter((p) => p.isActive).length} a la venta{rows.some((p) => !p.isActive) ? ` · ${rows.filter((p) => !p.isActive).length} apagado(s)` : ''}.
            {inventario && <> Semáforo: <span className="inline-block h-2 w-2 rounded-full bg-red-500 align-middle" /> rojo = surtir ya · <span className="inline-block h-2 w-2 rounded-full bg-amber-400 align-middle" /> ámbar = pronto · gris = sin umbral.</>}
          </p>
        </>
      )}

      {ficha && (
        <ProductoFicha product={ficha === 'nuevo' ? null : ficha} categorias={categorias} readOnly={!puedeEditar}
          inventario={inventario} puedeEntrada={puedeEntrada}
          onClose={() => setFicha(null)}
          onSaved={(msg) => { if (msg) toast.success(msg); setFicha(null); reload(); }} />
      )}
      {verCats && <CategoriasModal rows={rows} onClose={() => setVerCats(false)} onChanged={reload} />}
      {verCompra && <QueComprarModal rows={rows} onClose={() => setVerCompra(false)} onAbrir={(p) => { setVerCompra(false); setFicha(p); }} />}
    </div>
  );
}
